import { styled, type SxProps, type Theme } from "@mui/material/styles";

import { MenuList } from "ui/menu-list/menu-list";

import {
  MENU_DIVIDER_SPACING,
  MENU_PANEL_MIN_WIDTH,
  MENU_PANEL_SHADOW,
  MENU_PANEL_SPACING,
} from "ui/actions-menu/actions-menu.constants";

import { borderRadius, zIndex } from "design-tokens";

/** No MUI `zIndex` slot fits a non-modal popper, so the Apollo scale is read directly. */
export const POPPER_SX: SxProps<Theme> = { zIndex: zIndex.floating };

const SCROLLBAR_SIZE = 8;
const SCROLLBAR_RADIUS = 4;

export const WRAPPER_SX: SxProps<Theme> = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

/**
 * Keyed on the `aria-expanded` that `ActionsMenu` injects, so the button needs no open state of its
 * own. `chevron-down.svg` is symmetric, so the rotated glyph is the "up" one — the same flip
 * `ui/select` gets from MUI's `.MuiAutocomplete-popupIndicatorOpen`.
 */
export const MENU_BUTTON_SX: SxProps<Theme> = {
  '&[aria-expanded="true"] .MuiButton-endIcon': { transform: "rotate(180deg)" },
};

/**
 * The menu's own inset rule. `Divider` already brings the colour, the hairline and `margin: 0`; only
 * the 12px gutter is ours. The vertical 8px is stated for the case where no `MenuItem` precedes the
 * divider — where one does, MUI's `& + .MuiDivider-root` rule sets the same value from a stronger
 * selector.
 */
export const DIVIDER_SX: SxProps<Theme> = {
  my: MENU_DIVIDER_SPACING.y,
  mx: MENU_DIVIDER_SPACING.x,
};

/**
 * A `MenuList` rather than a plain `Box`, because arrow keys, Home/End and typeahead all live on
 * MUI's roving tab index and items only join it through `MenuListContext`. The `div` root and the
 * padding this relies on are defaults of `ui/menu-list`, which documents why each one matters.
 */
export const MenuPanel = styled(MenuList)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(MENU_PANEL_SPACING.gap),
  // Only safe next to `disablePadding`; `List` otherwise sets its 8px as longhands this cannot outrank.
  padding: theme.spacing(MENU_PANEL_SPACING.padding),
  minWidth: MENU_PANEL_MIN_WIDTH,
  width: "max-content",
  /**
   * The viewport, not a fixed step: a menu long enough to need scrolling is rare, and a 400px cap
   * put a scrollbar on the touchpoint preview menu while it still had room on screen. 16px is the
   * 8px `preventOverflow` padding at both ends, so the cap and the popper agree on where the edge is.
   */
  maxHeight: "calc(100vh - 16px)",
  overflowX: "hidden",
  overflowY: "auto",
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.border.primary}`,
  borderRadius: borderRadius.md,
  boxShadow: MENU_PANEL_SHADOW,
  "&::-webkit-scrollbar": {
    width: SCROLLBAR_SIZE,
    height: SCROLLBAR_SIZE,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.divider,
    borderRadius: SCROLLBAR_RADIUS,
    border: `1px solid ${theme.palette.divider}`,
  },
}));
